import { ArrowUpRight, ArrowRight, Edit, ExternalLink, Link, Trash } from '@styled-icons/feather';
import { CreditCard, PlayCircleOutline } from '@styled-icons/material';
import { ViewAgenda, Article, Info } from '@styled-icons/material-outlined';
import { DEFAULT_ACCENT_NAME, DreamBlock } from '@typedream/core';
import { focusEditor, isCollapsed, setNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';
import shallow from 'zustand/shallow';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { getURL, isLinkableElement, isTextBlock, getElement } from '../../../editorTools/queries';
import { removeElementLink, updateElementLink } from '../../../editorTools/transforms';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import {
  removeLinkAtSelection,
  toggleOpenInNewTabAtSelection,
  upsertLinkAtSelection,
} from '../../../nodes/link/transforms';
import { useDreamEditorStore } from '../../../plugins/store';
import { addStyleAtSelection } from '../../../nodes/text/transforms';
import styles from '../SideToolbar.module.css';
import { ELEMENT_IMAGE, KEY_COLLECTION_CONNECT_DATA, KEY_INTERNAL_LINK } from '../../../pluginKeys';
import { InternalLinkOptions } from '../../../plugins/internalLink/types';
import { isUpHotKey, isDownHotKey } from '../../../hotkeys';
import ConnectedToDataIndicator from './ConnectData/ConnectedToDataIndicator';
import { BLOCK_ELEMENT_KEY } from '../../../nodes/collection/components/CollectionDataElement/types';
import { CollectionDataPluginOptions } from '../../../nodes/collection/types';
import { Floating } from '../../../components/Floating';
import { animated, useSpring, useTransition } from 'react-spring';
import { offset, shift } from '@floating-ui/react-dom';
import Image from 'next/image';

interface LinkInputProps {
  initialValue?: string;
  internalPages?: InternalLinkOptions['pagesPaths'];
  onSave: (url: string, pageID?: string, pagePath?: string) => void;
}

const StyledHr = styled.hr`
  background-color: var(--text-lighter);
  height: 1px;
  opacity: 0.2;
  width: 100%;
  margin: 0px;
`;

const StyledSuggestionContainer = styled.div`
  border-radius: 5px;
  margin-top: 1rem;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const StyledSuggestionOption = styled.div<{
  isHovered?: boolean;
}>`
  padding: 10px;
  border-radius: 5px;
  line-height: 1.2;
  margin-bottom: 1rem;
  cursor: pointer;

  &:hover {
    background-color: var(--background-highlight);
  }

  ${(props) => (props.isHovered ? 'background-color: var(--background-highlight);' : '')}
`;

const StyledButton = styled.button`
  all: unset;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
  border: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 13px;
  padding: calc(0.5em - 1px) 1em;
  cursor: pointer;
  border-radius: 4px;
  background-color: var(--neutral-100);
  border-color: var(--neutral-100);
  color: var(--neutral-700);
`;

const getNextOption = (max: number, currIndex: number) => {
  if (currIndex === null || currIndex === undefined) return 0;
  return (currIndex + 1) % max;
};

const getPrevOption = (max: number, currIndex: number) => {
  if (currIndex === null || currIndex === undefined) return 0;
  const prevIndex = currIndex - 1 > 0 ? currIndex - 1 : max + (currIndex - 1);
  return prevIndex % max;
};

type LinkSuggestionProps = {
  onSave: LinkInputProps['onSave'];
  inputURL: string;
  internalPages: InternalLinkOptions['pagesPaths'];
};

export function LinkSuggestion(props: LinkSuggestionProps) {
  const { onSave, inputURL, internalPages } = props;

  const [suggestions, setSuggestions] = useState<Array<any>>([]);

  useEffect(() => {
    if (!inputURL || inputURL?.length <= 0 || !internalPages) {
      setSuggestions([]);
      return;
    }
    const newSuggestions = Object.values(internalPages).filter(
      (v: { name: string; path: string[]; childTemplatePage?: boolean }) => {
        if (v?.childTemplatePage) return false;
        const searchTerm = inputURL.toLowerCase();
        const pagePath = `/${v.path.join('/')}`;
        return (
          v?.name?.toLowerCase()?.includes(searchTerm) ||
          pagePath.toLowerCase().includes(searchTerm)
        );
      }
    );
    setSuggestions(newSuggestions);
  }, [inputURL]);

  const titleRef = useRef<HTMLDivElement>(null);
  const [menuMaxHeight, setMenuMaxHeight] = useState<string>();

  useEffect(() => {
    const calculateAndSetMaxHeight = debounce(() => {
      const top = titleRef?.current?.getBoundingClientRect()?.top;
      setMenuMaxHeight(`calc(100vh - ${(top || 0) + 100}px)`);
    }, 150);

    window.addEventListener('scroll', calculateAndSetMaxHeight);

    // this is hack to set maxHeight properly when it first enter viewport
    // it's when color option menu is clicked.
    // Can't set it on first mount, because at the first mount
    // the component is at position top around -10000px
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio === 1) {
          calculateAndSetMaxHeight();
        }
      },
      { threshold: [1] }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      window.removeEventListener('scroll', calculateAndSetMaxHeight);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <StyledSuggestionOption
        ref={titleRef}
        role="none"
        onClick={() => {
          // handle save
          onSave(inputURL);
        }}
      >
        <div className="with-text-color is-size-7">{inputURL}</div>
        <div className="with-lighter-text-color mt-1 is-size-7">
          <ArrowUpRight size={15} />
          External Link
        </div>
      </StyledSuggestionOption>

      <div
        style={{
          maxHeight: menuMaxHeight,
          transition: 'max-height 0.3s',
          overflowY: 'scroll',
        }}
      >
        {suggestions && suggestions?.length > 0 && (
          <>
            <StyledHr />
            <div className="with-lighter-text-color ml-2 mt-2 is-size-7">Link to page</div>
            {suggestions.map((s) => (
              <StyledSuggestionOption
                role="none"
                onClick={() => {
                  onSave(inputURL, s.id, s.path.join('/'));
                }}
                key={s.id}
              >
                <div className="with-text-color is-size-7">{s.name}</div>
                <div className="with-lighter-text-color is-size-7">/{s.path.join('/')}</div>
              </StyledSuggestionOption>
            ))}
          </>
        )}
      </div>
    </>
  );
}

function LinkInput(props: LinkInputProps) {
  const { initialValue, internalPages, onSave } = props;

  const [inputURL, setInputURL] = useState(initialValue || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const [suggestions, setSuggestions] = useState<Array<any>>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);

  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [isFormsOpen, setIsFormsOpen] = useState(false);
  const [isScrollOpen, setIsScrollOpen] = useState(false);
  const refProduct = useRef<HTMLDivElement>(null);
  const refForm = useRef<HTMLDivElement>(null);
  const refScroll = useRef<HTMLDivElement>(null);

  const createMouseLeaveHandler = (setOpen: (open: boolean) => void) => () => {
    const id = setTimeout(() => {
      setOpen(false);
    }, 500);
    setTimeoutId(id);
  };

  const openNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  const createMouseOverHandler =
    (setOpen: (open: boolean) => void, otherSetOpen: (open: boolean) => void) => () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      otherSetOpen(false);
      setOpen(true);
    };

  const handleMouseLeaveProducts = createMouseLeaveHandler(setIsProductsOpen);
  const handleMouseLeaveForms = createMouseLeaveHandler(setIsFormsOpen);
  const handleMouseLeaveScroll = createMouseLeaveHandler(setIsScrollOpen);

  const handleMouseOverProducts = createMouseOverHandler(setIsProductsOpen, () => {
    setIsFormsOpen(false);
    setIsScrollOpen(false);
  });

  const handleMouseOverForms = createMouseOverHandler(setIsFormsOpen, () => {
    setIsProductsOpen(false);
    setIsScrollOpen(false);
  });

  const handleMouseOverScroll = createMouseOverHandler(setIsScrollOpen, () => {
    setIsProductsOpen(false);
    setIsFormsOpen(false);
  });

  const productsSpringProps = useSpring({
    opacity: isProductsOpen ? 1 : 0,
  });

  const formsSpringProps = useSpring({
    opacity: isFormsOpen ? 1 : 0,
  });

  const scrollSpringProps = useSpring({
    opacity: isScrollOpen ? 1 : 0,
  });

  useEffect(() => inputRef.current?.focus(), []);

  useEffect(() => {
    // suggestions
    if (!inputURL || inputURL?.length <= 0 || !internalPages) {
      setSuggestions([]);
      return;
    }
    const newSuggestions = Object.values(internalPages).filter(
      (v: { name: string; path: string[]; childTemplatePage?: boolean }) => {
        if (v?.childTemplatePage) return false;
        const searchTerm = inputURL.toLowerCase();
        const pagePath = `/${v.path.join('/')}`;
        return (
          v?.name?.toLowerCase()?.includes(searchTerm) ||
          pagePath.toLowerCase().includes(searchTerm)
        );
      }
    );
    setSuggestions(newSuggestions);
  }, [inputURL]);

  const onSuggestionKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDownHotKey(e) || isUpHotKey(e)) {
      if (!suggestions || suggestions.length <= 0) return;
      e.preventDefault();
      setSelectedSuggestionIndex((prevIndex) =>
        isDownHotKey(e)
          ? getNextOption((suggestions?.length || 0) + 1, prevIndex)
          : getPrevOption((suggestions?.length || 0) + 1, prevIndex)
      );
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onSuggestionKeydown(event);
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();

    // handle selecting external link
    if (selectedSuggestionIndex === 0) {
      onSave(inputURL);
      return;
    }

    // handle selecting internal link
    if (suggestions[selectedSuggestionIndex - 1]) {
      const selectedSuggestion = suggestions[selectedSuggestionIndex - 1];
      onSave(inputURL, selectedSuggestion.id, selectedSuggestion.path.join('/'));
    }
  };

  return (
    <>
      <form
        className={styles.menuGroup}
        onSubmit={(e) => {
          e.preventDefault();
          onSave(inputURL);
        }}
      >
        <input
          className={classNames(styles.menuItem, styles.flex1)}
          ref={inputRef}
          type="text"
          onKeyDown={onKeyDown}
          value={inputURL}
          onChange={(e) => setInputURL(e.target.value)}
          placeholder="Paste link or search page"
          onFocus={() => setInputFocused(true)}
        />

        <button
          type="submit"
          className={classNames(styles.menuItem)}
          disabled={inputURL === initialValue}
        >
          <div className="p-1">Save</div>
        </button>
      </form>
      {inputURL && (
        <StyledSuggestionContainer>
          <StyledSuggestionOption
            isHovered={selectedSuggestionIndex === 0}
            role="none"
            onClick={() => {
              // handle save
              onSave(inputURL);
            }}
          >
            <div className={classNames(styles.ellipsis, 'with-text-color is-size-7')}>
              {inputURL}
            </div>
            <div className="with-lighter-text-color mt-1 is-size-7">
              <ArrowUpRight size={15} />
              External Link
            </div>
          </StyledSuggestionOption>

          {suggestions && suggestions?.length > 0 && (
            <>
              <StyledHr />
              <div className="with-lighter-text-color ml-2 mt-2 is-size-7">Link to page</div>
              {suggestions.map((s, i) => (
                <StyledSuggestionOption
                  isHovered={i + 1 === selectedSuggestionIndex}
                  role="none"
                  onClick={() => {
                    onSave(inputURL, s.id, s.path.join('/'));
                  }}
                  key={s.id}
                >
                  <div className="with-text-color is-size-7">{s.name}</div>
                  <div className="with-lighter-text-color is-size-7">/{s.path.join('/')}</div>
                </StyledSuggestionOption>
              ))}
            </>
          )}
        </StyledSuggestionContainer>
      )}
      {inputFocused && (
        <>
          <div className={styles.discoveryContainer}>
            <div
              ref={refProduct}
              className={styles.featureDiscovery}
              onMouseOver={handleMouseOverProducts}
              onMouseLeave={handleMouseLeaveProducts}
              onClick={() =>
                openNewTab('https://typedream.com/docs/how-to-sell-digital-products-on-typedream')
              }
            >
              <animated.div style={productsSpringProps}>
                <Floating
                  reference={refProduct}
                  show={isProductsOpen}
                  options={{
                    strategy: 'fixed',
                    placement: 'left',
                    middleware: [offset(24), shift()],
                  }}
                  className={styles.featureFloatingContainer}
                  autoUpdate
                >
                  <div className={styles.floatingContainerInner}>
                    <Image
                      src="/images/feature-discovery-products.png"
                      alt="Products Preview"
                      width={200}
                      height={260}
                    />
                    <div className={styles.discoveryTitle}>Sell Digital Products</div>
                    <div className={styles.discoverySub}>
                      Freebies, Ebook, Courses, Notion Templates, Canva Templates
                    </div>
                    <StyledButton className={styles.discoveryButton}>
                      <div className="text-secondary">
                        <Article size={16} className="mr-2" />
                        Read Tutorial
                      </div>
                    </StyledButton>
                  </div>
                </Floating>
              </animated.div>
              <CreditCard size={16} className={styles.accentColor} />
              <div className="p-1">Sell products</div>
              <ArrowRight size={16} />
            </div>
            <div
              className={styles.featureDiscovery}
              onMouseOver={handleMouseOverForms}
              onMouseLeave={handleMouseLeaveForms}
              ref={refForm}
              onClick={() =>
                openNewTab('https://typedream.com/docs/how-to-create-a-form-on-typedream')
              }
            >
              <animated.div style={formsSpringProps}>
                <Floating
                  reference={refForm}
                  show={isFormsOpen}
                  options={{
                    strategy: 'fixed',
                    placement: 'left',
                    middleware: [offset(24), shift()],
                  }}
                  className={styles.featureFloatingContainer}
                  autoUpdate
                >
                  <div className={styles.floatingContainerInner}>
                    <Image
                      src="/images/feature-discovery-form.png"
                      alt="Forms Preview"
                      width={200}
                      height={260}
                    />
                    <div className={styles.discoveryTitle}>Build a Form</div>
                    <div className={styles.discoverySub}>
                      A dedicated page for service or order forms, applications, etc.
                    </div>
                    <StyledButton className={styles.discoveryButton}>
                      <div>
                        <Article size={16} className="mr-2" />
                        Read Tutorial
                      </div>
                    </StyledButton>
                  </div>
                </Floating>
              </animated.div>
              <ViewAgenda size={16} className={styles.accentColor} />
              <div className="p-1">Add forms</div>
              <ArrowRight size={16} />
            </div>
            <div
              className={styles.featureDiscovery}
              onMouseOver={handleMouseOverScroll}
              onMouseLeave={handleMouseLeaveScroll}
              ref={refScroll}
              onClick={() => openNewTab('https://typedream.com/docs/link-to-section')}
            >
              <animated.div style={scrollSpringProps}>
                <Floating
                  reference={refScroll}
                  show={isScrollOpen}
                  options={{
                    strategy: 'fixed',
                    placement: 'left',
                    middleware: [offset(24), shift()],
                  }}
                  className={styles.featureFloatingContainer}
                  autoUpdate
                >
                  <div className={styles.floatingContainerInner}>
                    <Image
                      src="/images/feature-discovery-scroll.png"
                      alt="Scroll Preview"
                      width={200}
                      height={260}
                    />
                    <div className={styles.discoveryTitle}>Link to a section</div>
                    <div className={styles.discoverySub}>
                      Learn how to create a link that scrolls smoothly to a specific section in your
                      page.
                    </div>
                    <StyledButton className={styles.discoveryButton}>
                      <div>
                        <Article size={16} className="mr-2" />
                        Read Tutorial
                      </div>
                    </StyledButton>
                  </div>
                </Floating>
              </animated.div>
              <Info size={16} />
              <div className="p-1">How to scroll to specific section?</div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

LinkInput.defaultProps = {
  initialValue: '',
  internalPages: undefined,
};

function ToolbarLink({ setToolbarTab }: { setToolbarTab: (tab: string) => void }) {
  const editor = useTPlateEditorRef();

  const { options } = getTPlugin<InternalLinkOptions>(editor, KEY_INTERNAL_LINK);
  const { options: opt } = getTPlugin<CollectionDataPluginOptions>(
    editor,
    KEY_COLLECTION_CONNECT_DATA
  );
  const { showSchema } = opt as CollectionDataPluginOptions;
  const [isAddLinkDisabled, elementURL, isOpenInNewTab, internalLinkPageID] = useDreamEditorStore(
    () => {
      const selectedElementEntry = getSelectedElementEntry(editor);

      if (!selectedElementEntry) return [];

      const [selectedElement] = selectedElementEntry;

      const disableAddLink =
        (!editor.selection || isCollapsed(editor.selection)) && !isLinkableElement(selectedElement);

      let elementWithUrl:
        | (DreamBlock & {
            // Somehow need to explicitly add these properties
            // to make it work.
            open_in_new_tab?: boolean;
            page_id?: string;
          })
        | null = null;
      if (isLinkableElement(selectedElement)) {
        elementWithUrl = selectedElement;
      } else if (isTextBlock(editor, selectedElement)) {
        const linkElement = getElement(editor);
        elementWithUrl = linkElement?.type === 'a' ? linkElement : null;
      }

      const url = elementWithUrl ? getURL(elementWithUrl) : undefined;
      const openInNewTab = elementWithUrl?.open_in_new_tab;
      const pageID = elementWithUrl?.page_id;

      return [disableAddLink, url, openInNewTab, pageID];
    },
    shallow
  );

  const renderCMSDataIndicator = () => {
    const elementEntry = getSelectedElementEntry(editor);
    if (!elementEntry) return null;
    const [element, path] = elementEntry;

    const blockElementKey =
      element.type === ELEMENT_IMAGE ? BLOCK_ELEMENT_KEY.HREF : BLOCK_ELEMENT_KEY.URL;

    return (
      <ConnectedToDataIndicator
        element={element}
        onClose={() => {
          setToolbarTab('advanced');
          // onReset();
        }}
        blockElementKey={blockElementKey}
        path={path}
        containerClassnames={styles.dataIndicatorOverride}
      />
    );
  };
  const [isEditing, setIsEditing] = useState(false);

  const saveLink = (link: string, pageID?: string, pagePath?: string) => {
    const elementEntry = getSelectedElementEntry(editor);
    if (!elementEntry) return;
    const [element, path] = elementEntry;

    if (isLinkableElement(element)) {
      updateElementLink(editor, { url: link, pageID, pagePath }, { at: path });
    } else {
      upsertLinkAtSelection(editor, { url: link, pageID, pagePath });
      addStyleAtSelection(editor, {
        color: DEFAULT_ACCENT_NAME,
        gradient: undefined,
      });
    }

    setIsEditing(false);
    focusEditor(editor);
  };

  const toggleOpenInNewTab = () => {
    const elementEntry = getSelectedElementEntry(editor);
    if (!elementEntry) return;
    const [element, path] = elementEntry;

    if (isLinkableElement(element)) {
      setNodes(editor, { open_in_new_tab: !element.open_in_new_tab }, { at: path });
    } else {
      toggleOpenInNewTabAtSelection(editor);
    }

    focusEditor(editor);
  };

  const removeLink = () => {
    const elementEntry = getSelectedElementEntry(editor);
    if (!elementEntry) return;
    const [element, path] = elementEntry;

    if (isLinkableElement(element)) {
      removeElementLink(editor, { at: path });
    } else {
      removeLinkAtSelection(editor);
    }

    focusEditor(editor);
  };

  const currentLinkPageName = internalLinkPageID
    ? options?.pagesPaths?.[internalLinkPageID || '']?.name
    : undefined;

  const renderLink = () => {
    if (isEditing) {
      return (
        <LinkInput
          initialValue={elementURL}
          internalPages={options?.pagesPaths}
          onSave={saveLink}
        />
      );
    }

    if (!elementURL && !internalLinkPageID) {
      return (
        <button
          type="button"
          className={styles.button}
          disabled={isAddLinkDisabled}
          onClick={() => setIsEditing(true)}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link size={16} className="mr-2" />
          <span>Add Link</span>
        </button>
      );
    }

    return (
      <div className={styles.card}>
        <div className="is-flex is-align-items-center" style={{ gap: 12 }}>
          <div className={classNames(styles.small, styles.flex1)}>
            <div className={classNames(styles.ellipsis, 'with-text-color mb-1')}>
              {elementURL || '/'}
            </div>
            {currentLinkPageName ? `Page: ${currentLinkPageName}` : 'External Link'}
          </div>
          <div className="is-flex is-align-items-center">
            <a href={elementURL} target="_new" className={styles.icon}>
              <ExternalLink size={16} />
            </a>
            <button type="button" className={styles.icon} onClick={() => setIsEditing(true)}>
              <Edit size={16} />
            </button>
            <button type="button" className={styles.icon} onClick={removeLink}>
              <Trash size={16} />
            </button>
          </div>
        </div>

        <div className="is-flex is-align-items-center mt-1">
          <div className={classNames(styles.small, styles.flex1)}>Open in new tab</div>
          <div className={styles.switchContainer}>
            <input
              id="toolbar-link-input"
              type="checkbox"
              name="publish"
              className="switch is-rounded is-small is-primary"
              checked={isOpenInNewTab}
              onChange={toggleOpenInNewTab}
            />
            <label htmlFor="toolbar-link-input" style={{ verticalAlign: 'middle' }}>
              {/* no text label */}
            </label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.section}>
      <span className={styles.label}>Link</span>
      {renderLink()}
      {showSchema && renderCMSDataIndicator()}
    </div>
  );
}

export default memo(ToolbarLink);
